/**
 * Color utility functions
 */

/**
 * Get appropriate text color for a background color
 * Uses relative luminance to determine if text should be white or black
 * 
 * @param {string} backgroundColor - Background color (hex or rgb/rgba)
 * @returns {string} - Text color ('white' or 'black')
 */
export const getTextColor = (backgroundColor) => {
    // Default to black text if no color provided
    if (!backgroundColor) return 'black';

    let r, g, b;

    // Handle hex colors
    if (backgroundColor.startsWith('#')) {
        // Convert hex to RGB
        let hex = backgroundColor.substring(1);

        // Handle shorthand hex (#FFF)
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }

        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
    }
    // Handle rgb/rgba
    else if (backgroundColor.startsWith('rgb')) {
        const matches = backgroundColor.match(/(\d+)/g);
        if (matches && matches.length >= 3) {
            r = parseInt(matches[0], 10);
            g = parseInt(matches[1], 10);
            b = parseInt(matches[2], 10);
        } else {
            return 'black'; // Default if can't parse
        }
    }
    // Default for unknown formats
    else {
        return 'black';
    }

    // Calculate relative luminance using WCAG formula
    const toLinear = (c) => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    const R = toLinear(r);
    const G = toLinear(g);
    const B = toLinear(b);

    const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

    // Return white for dark backgrounds, black for light backgrounds
    return luminance > 0.5 ? 'black' : 'white';
};

/**
 * Generate a gradient string from an array of colors
 * 
 * @param {Array} colors - Array of color strings
 * @param {string} direction - Gradient direction (e.g., '135deg')
 * @returns {string} - CSS gradient string
 */
export const generateGradient = (colors, direction = '135deg') => {
    if (!colors || colors.length === 0) return 'transparent';
    if (colors.length === 1) return colors[0];

    return `linear-gradient(${direction}, ${colors.join(', ')})`;
};

/**
 * Adjust color brightness
 * 
 * @param {string} color - Hex color
 * @param {number} percent - Percent to lighten (positive) or darken (negative)
 * @returns {string} - Adjusted hex color
 */
export const adjustBrightness = (color, percent) => {
    if (!color) return color;
    if (!color.startsWith('#')) return color;

    let hex = color.substring(1);
    if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
    }

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const adjustColor = (c) => {
        return Math.max(0, Math.min(255, Math.round(c * (1 + percent / 100))));
    };

    const rr = adjustColor(r).toString(16).padStart(2, '0');
    const gg = adjustColor(g).toString(16).padStart(2, '0');
    const bb = adjustColor(b).toString(16).padStart(2, '0');

    return `#${rr}${gg}${bb}`;
};